import {
  findQuestionByKey,
  type InspectionAnswerValue,
  type InspectionPdfLayout,
  type InspectionPrefillSource,
  type InspectionQuestion,
  type InspectionSchema,
} from '@sitewise/shared';

import type { InspectionAnswerSummary } from '../../../api/inspections.js';
import type { BuildingSummary } from '../../../api/buildings.js';

export type RunnerViewMode = 'table' | 'card';

export interface PrefillContext {
  building: Pick<BuildingSummary, 'name' | 'addressLine1' | 'city' | 'state' | 'postalCode'> | null;
  unitNumber: string | null;
  inspectorName: string | null;
  inspectionDate: string | null;
}

export function formatInspectionDate(iso: string | null | undefined): string | null {
  if (!iso) return null;
  const d = new Date(iso);
  if (Number.isNaN(d.getTime())) return null;
  return d.toLocaleDateString('en-US', { month: '2-digit', day: '2-digit', year: 'numeric' });
}

export function formatBuildingAddress(
  building: PrefillContext['building'],
): string | null {
  if (!building) return null;
  const parts = [
    building.addressLine1,
    [building.city, building.state, building.postalCode].filter(Boolean).join(', '),
  ].filter(Boolean);
  return parts.join(', ') || null;
}

export function resolvePrefillValue(
  source: InspectionPrefillSource,
  ctx: PrefillContext,
): string | null {
  switch (source) {
    case 'building.name':
      return ctx.building?.name ?? null;
    case 'building.address':
      return formatBuildingAddress(ctx.building);
    case 'unit.number':
      return ctx.unitNumber;
    case 'inspection.date':
      return ctx.inspectionDate;
    case 'inspector.name':
      return ctx.inspectorName;
  }
}

export function buildPrefillValue(
  question: InspectionQuestion,
  ctx: PrefillContext,
): InspectionAnswerValue | null {
  if (question.kind === 'short_text' && question.prefillSource) {
    const text = resolvePrefillValue(question.prefillSource, ctx);
    return text ? { kind: 'short_text', value: text } : null;
  }
  if (question.kind === 'number' && question.prefillSource) {
    return null;
  }
  return null;
}

export function isChecklistSection(sectionTitle: string): boolean {
  const header = sectionTitle.toLowerCase();
  return (
    !header.includes('inspection information') &&
    !header.includes('summary') &&
    !header.includes('sign-off')
  );
}

export function countChecklistDefects(
  schema: InspectionSchema,
  answers: InspectionAnswerSummary[],
): number {
  const checklistQuestionIds = new Set<string>();
  for (const section of schema.sections) {
    if (!isChecklistSection(section.title)) continue;
    for (const q of section.questions) {
      if (q.kind !== 'section_header') checklistQuestionIds.add(q.id);
    }
  }
  return answers.filter((a) => a.isDefect && checklistQuestionIds.has(a.questionId)).length;
}

export function countResolvedDefectWorkOrders(answers: InspectionAnswerSummary[]): number {
  return answers.filter((a) => a.isDefect && a.defectResolvedByWorkOrderId).length;
}

export function footerSyncTargets(schema: InspectionSchema): {
  repairsNeededId: string | null;
  workOrdersSubmittedId: string | null;
  woDateSubmittedId: string | null;
} {
  return {
    repairsNeededId: findQuestionByKey(schema, 'footer.unit_repairs_needed')?.id ?? null,
    workOrdersSubmittedId:
      findQuestionByKey(schema, 'footer.work_orders_submitted')?.id ?? null,
    woDateSubmittedId: findQuestionByKey(schema, 'footer.wo_date_submitted')?.id ?? null,
  };
}

export function resolvePdfLayouts(schema: InspectionSchema): InspectionPdfLayout[] {
  if (schema.pdfLayouts?.length) return schema.pdfLayouts;
  if (schema.pdfLayout) return [schema.pdfLayout];
  return ['default'];
}

export function yesNoLabel(value: 'yes' | 'no' | 'na' | null | undefined): string {
  if (value === 'yes') return 'Yes';
  if (value === 'no') return 'No';
  if (value === 'na') return 'N/A';
  return '—';
}
